import { useState } from 'react';
import { useHebergement } from '@/hooks/useHebergement';
import AppGate from '@/components/feature/AppGate';
import OverviewView from './components/OverviewView';
import PropertiesView from './components/PropertiesView';
import UnitsView from './components/UnitsView';
import CalendarView from './components/CalendarView';
import ReservationsView from './components/ReservationsView';
import ReservationFormModal, { type ReservationPrefill } from './components/ReservationFormModal';
import HebergementCategoriesModal from './components/HebergementCategoriesModal';

type Tab = 'overview' | 'properties' | 'units' | 'calendar' | 'reservations';

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'overview', label: "Vue d'ensemble", icon: 'ri-dashboard-line' },
  { key: 'properties', label: 'Mes propriétés', icon: 'ri-building-2-line' },
  { key: 'units', label: 'Chambres / unités', icon: 'ri-door-open-line' },
  { key: 'calendar', label: 'Calendrier & dispo', icon: 'ri-calendar-line' },
  { key: 'reservations', label: 'Réservations', icon: 'ri-calendar-check-line' },
];

function HebergementInner() {
  const h = useHebergement();
  const [tab, setTab] = useState<Tab>('overview');
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);
  const [reservationModal, setReservationModal] = useState<{ prefill: ReservationPrefill | null } | null>(null);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  const openReservation = (prefill: ReservationPrefill | null) => setReservationModal({ prefill });

  const goAddUnit = (propertyId: number) => {
    setSelectedPropertyId(propertyId);
    setTab('units');
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-hotel-line text-primary-500"></i>Zifek Hébergement
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Propriétés, unités, disponibilités et réservations — connectées à votre site Zifek.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setCategoriesOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-background-50 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-price-tag-3-line"></i>Catégories
          </button>
          <button
            onClick={() => openReservation(null)}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>Nouvelle réservation
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit overflow-x-auto max-w-full mb-5">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1.5 ${
              tab === t.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={`${t.icon} text-sm`}></i>{t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {h.loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : h.error ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
          <p className="text-foreground-600 mb-3 text-sm">{h.error}</p>
          <button onClick={h.reload} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap">
            Réessayer
          </button>
        </div>
      ) : tab === 'overview' ? (
        <OverviewView
          stats={h.stats}
          upcomingReservations={h.upcomingReservations}
          currency={h.currency}
          propertyName={h.propertyName}
          unitName={h.unitName}
          onGoToTab={(t) => setTab(t as Tab)}
          onNewReservation={() => openReservation(null)}
        />
      ) : tab === 'properties' ? (
        <PropertiesView
          properties={h.properties}
          unitsFor={h.unitsFor}
          mediaFor={h.mediaFor}
          amenitiesFor={h.amenitiesFor}
          currency={h.currency}
          categories={h.categories}
          createCategory={h.createCategory}
          createProperty={h.createProperty}
          updateProperty={h.updateProperty}
          toggleFeatured={h.toggleFeatured}
          deleteProperty={h.deleteProperty}
          savePropertyMedia={h.savePropertyMedia}
          savePropertyAmenities={h.savePropertyAmenities}
          onAddUnit={goAddUnit}
        />
      ) : tab === 'units' ? (
        <UnitsView
          properties={h.properties}
          units={h.units}
          currency={h.currency}
          selectedPropertyId={selectedPropertyId}
          onSelectProperty={setSelectedPropertyId}
          createUnit={h.createUnit}
          updateUnit={h.updateUnit}
          deleteUnit={h.deleteUnit}
        />
      ) : tab === 'calendar' ? (
        <CalendarView
          properties={h.properties}
          units={h.units}
          blocks={h.blocks}
          reservations={h.reservations}
          currency={h.currency}
          createBlock={h.createBlock}
          deleteBlock={h.deleteBlock}
          checkAvailability={h.checkAvailability}
          onReserve={(prefill) => openReservation(prefill)}
        />
      ) : (
        <ReservationsView
          reservations={h.reservations}
          currency={h.currency}
          propertyName={h.propertyName}
          unitName={h.unitName}
          updateReservationStatus={h.updateReservationStatus}
          updateReservationPayment={h.updateReservationPayment}
          deleteReservation={h.deleteReservation}
          onNew={() => openReservation(null)}
        />
      )}

      {reservationModal && (
        <ReservationFormModal
          properties={h.properties}
          units={h.units}
          currency={h.currency}
          prefill={reservationModal.prefill}
          checkAvailability={h.checkAvailability}
          createReservation={h.createReservation}
          onClose={() => setReservationModal(null)}
          onCreated={() => { /* reload déjà fait par le hook */ }}
        />
      )}

      {categoriesOpen && (
        <HebergementCategoriesModal
          categories={h.categories}
          createCategory={h.createCategory}
          updateCategory={h.updateCategory}
          deleteCategory={h.deleteCategory}
          onClose={() => setCategoriesOpen(false)}
        />
      )}
    </div>
  );
}

export default function HebergementPage() {
  return (
    <AppGate
      nompage="hebergement"
      appName="Zifek Hébergement"
      icon="ri-hotel-line"
      appDescription="Gérez hôtels, riads, maisons d'hôtes, appartements, villas, studios et chambres : propriétés, unités, disponibilités, blocages, réservations et paiements. Installez l'app depuis l'AppStore pour commencer."
    >
      <HebergementInner />
    </AppGate>
  );
}